import { useEffect, useState } from "react";
import clsx from "clsx";
import Button from "../../ui/Button/Button";
import PasswordField from "../../ui/PasswordField/PasswordField";
import UsLocationFields from "../../ui/UsLocationFields/UsLocationFields";
import type { AccountType, SignupRequestDto, SignupRole } from "../../../types/types";
import { MAX_AGE_ADULT, MAX_AGE_TEEN, MIN_AGE_ADULT, MIN_AGE_TEEN, signupRoleOptions } from "./constants";
import { calculateAge } from "./utils";
import { parseIsoDate } from "../../../utils/DateUtils";
import { normalizePersonName } from "../../../utils/NameUtils";

export type SignupFormProps = {
  onSubmit: (payload: SignupRequestDto) => void | Promise<void>;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  submitLabel?: string;
  initialEmail?: string;
  initialRole?: SignupRole | "";
  initialAccountType?: AccountType;
  lockEmail?: boolean;
  lockRole?: boolean;
  lockAccountType?: boolean;
  onRoleChange?: (role: SignupRole) => void;
};

const monthOptions = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
] as const;

const getDaysInMonth = (year: string, month: string) => {
  if (!year || !month) {
    return 31;
  }

  const parsedYear = Number.parseInt(year, 10);
  const parsedMonth = Number.parseInt(month, 10);
  if (!Number.isInteger(parsedYear) || !Number.isInteger(parsedMonth)) {
    return 31;
  }

  return new Date(parsedYear, parsedMonth, 0).getDate();
};

const SignupForm = ({
  onSubmit,
  isSubmitting = false,
  errorMessage = null,
  submitLabel = "Sign up",
  initialEmail = "",
  initialRole = "",
  initialAccountType = "PERSONAL",
  lockEmail = false,
  lockRole = false,
  lockAccountType = false,
  onRoleChange,
}: SignupFormProps) => {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [role, setRole] = useState<SignupRole | "">(initialRole);
  const [accountType, setAccountType] = useState<AccountType>(initialAccountType);
  const [businessName, setBusinessName] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [businessWebsite, setBusinessWebsite] = useState("");
  const [businessVolunteerApprovalRequested, setBusinessVolunteerApprovalRequested] = useState(false);
  const [bio, setBio] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const today = new Date();
  const yearRangeStart = today.getUTCFullYear() - (role === "TEEN" ? MAX_AGE_TEEN : MAX_AGE_ADULT);
  const yearRangeEnd = today.getUTCFullYear() - (role === "TEEN" ? MIN_AGE_TEEN : MIN_AGE_ADULT);
  const yearOptions = Array.from(
    { length: Math.max(0, yearRangeEnd - yearRangeStart + 1) },
    (_, index) => String(yearRangeEnd - index),
  );
  const dayOptions = Array.from(
    { length: getDaysInMonth(birthYear, birthMonth) },
    (_, index) => String(index + 1).padStart(2, "0"),
  );
  const dateOfBirth = birthYear && birthMonth && birthDay ? `${birthYear}-${birthMonth}-${birthDay}` : "";

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  useEffect(() => {
    setRole(initialRole);
  }, [initialRole]);

  useEffect(() => {
    setAccountType(initialAccountType);
  }, [initialAccountType]);

  useEffect(() => {
    setBirthMonth("");
    setBirthDay("");
    setBirthYear("");
  }, [role]);

  useEffect(() => {
    if (role !== "ADULT") {
      setAccountType("PERSONAL");
      setBusinessName("");
      setBusinessDescription("");
      setBusinessWebsite("");
      setBusinessVolunteerApprovalRequested(false);
    }
  }, [role]);

  useEffect(() => {
    if (role !== "TEEN") {
      setBio("");
    }
  }, [role]);

  useEffect(() => {
    if (accountType !== "BUSINESS") {
      setBusinessVolunteerApprovalRequested(false);
    }
  }, [accountType]);

  useEffect(() => {
    if (birthDay && Number.parseInt(birthDay, 10) > getDaysInMonth(birthYear, birthMonth)) {
      setBirthDay("");
    }
  }, [birthDay, birthMonth, birthYear]);

  const hasResolvedSignupLocation = city.trim().length > 0 && state.trim().length === 2 && zip.trim().length > 0;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsedDateOfBirth = parseIsoDate(dateOfBirth);
    const normalizedFirstName = normalizePersonName(firstName);
    const normalizedLastName = normalizePersonName(lastName);

    if (password !== confirmPassword) {
      setLocalError("Passwords must match.");
      return;
    }

    if (!parsedDateOfBirth) {
      setLocalError("Please enter a valid date of birth.");
      return;
    }

    if (!role) {
      setLocalError("Please select whether you are signing up as a teen or adult.");
      return;
    }

    if (!normalizedFirstName || !normalizedLastName) {
      setLocalError("Please enter your first and last name.");
      return;
    }

    if (role === "ADULT" && accountType === "BUSINESS" && businessName.trim().length === 0) {
      setLocalError("Please enter your business name.");
      return;
    }

    if (!hasResolvedSignupLocation) {
      setLocalError("Please enter a valid ZIP code.");
      return;
    }

    if (!/^\d{5}(?:-\d{4})?$/.test(zip.trim())) {
      setLocalError("Please enter a valid ZIP code.");
      return;
    }

    // Extra protection in case browser date constraints are bypassed.
    if (parsedDateOfBirth > today) {
      setLocalError("Date of birth cannot be in the future.");
      return;
    }

    const age = calculateAge(parsedDateOfBirth, today);

    if (role === "TEEN" && (age < MIN_AGE_TEEN || age > MAX_AGE_TEEN)) {
      setLocalError("Teen accounts must be between 14 and 19 years old.");
      return;
    }

    if (role === "ADULT" && (age < MIN_AGE_ADULT || age > MAX_AGE_ADULT)) {
      setLocalError("Adult accounts must be between 18 and 120 years old.");
      return;
    }

    if (!termsAccepted) {
      setLocalError("Please agree to the Terms of Service before signing up.");
      return;
    }

    setLocalError(null);
    await onSubmit({
      email: email.trim(),
      password,
      firstName: normalizedFirstName,
      lastName: normalizedLastName,
      city: city.trim(),
      state: state.trim().toUpperCase(),
      zip: zip.trim(),
      role,
      accountType: role === "ADULT" ? accountType : "PERSONAL",
      // Send as ISO string so backend can deserialize to a LocalDate.
      dateOfBirth,
      bio: role === "TEEN" ? bio.trim() || null : null,
      skills: [],
      businessName: role === "ADULT" && accountType === "BUSINESS" ? businessName.trim() : null,
      businessDescription: role === "ADULT" && accountType === "BUSINESS" ? businessDescription.trim() || null : null,
      businessWebsite: role === "ADULT" && accountType === "BUSINESS" ? businessWebsite.trim() || null : null,
      businessVolunteerApprovalRequested: role === "ADULT" && accountType === "BUSINESS" && businessVolunteerApprovalRequested,
      termsAccepted: true,
    });
  };

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-3 rounded-xl border border-myteengig-border bg-white p-4 shadow-sm sm:p-6">
      <section className="rounded-xl border border-myteengig-border bg-myteengig-background/60 p-3 sm:p-4">
        <fieldset>
          <legend id="signup-role-label" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
            Who are you joining as?
          </legend>
          <input id="signup-role" type="hidden" value={role} readOnly />
          <div className="grid gap-3 lg:grid-cols-2">
            {signupRoleOptions.map((signupRoleOption) => {
              const isSelected = role === signupRoleOption.value;

              return (
                <label
                  key={signupRoleOption.value}
                  className={clsx(
                    "block cursor-pointer rounded-2xl border bg-white p-3 transition sm:p-4",
                    isSelected
                      ? "border-myteengig-primary shadow-[0_0_0_2px_rgba(31,145,216,0.12)]"
                      : "border-myteengig-border hover:border-myteengig-primary/60 hover:bg-myteengig-primary/5",
                    lockRole && !isSelected && "cursor-not-allowed opacity-60 hover:border-myteengig-border hover:bg-white",
                  )}
                >
                  <input
                    type="radio"
                    name="signup-role-option"
                    value={signupRoleOption.value}
                    aria-label={signupRoleOption.title}
                    checked={isSelected}
                    disabled={lockRole && !isSelected}
                    onChange={(event) => {
                      const nextRole = event.target.value as SignupRole;
                      setRole(nextRole);
                      onRoleChange?.(nextRole);
                      setLocalError(null);
                    }}
                    className="sr-only"
                  />
                  <span className="block min-w-0">
                      <span className="block text-sm font-semibold text-myteengig-text-primary sm:text-base">{signupRoleOption.title}</span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-myteengig-text-muted sm:mt-1 sm:text-sm">
                        {signupRoleOption.description}
                      </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
        {lockRole && role ? (
          <p className="mt-1 text-xs font-medium text-myteengig-primary">Account type is locked from your invite.</p>
        ) : null}

        {role === "ADULT" ? (
          <div className="mt-4">
            <fieldset>
              <legend className="mb-2 block text-sm font-medium text-myteengig-text-primary">Adult account type</legend>
              <div className="grid gap-3 lg:grid-cols-2">
                {[
                  {
                    value: "PERSONAL" as const,
                    title: "Personal",
                    description: "For parents, guardians, homeowners, and other adults using the regular adult experience.",
                  },
                  {
                    value: "BUSINESS" as const,
                    title: "Business",
                    description: "For companies posting listings, reviewing applicants, and messaging accepted teens.",
                  },
                ].map((option) => {
                  const isSelected = accountType === option.value;
                  return (
                    <label
                      key={option.value}
                      className={clsx(
                        "block cursor-pointer rounded-2xl border bg-white p-3 transition sm:p-4",
                        isSelected
                          ? "border-myteengig-primary shadow-[0_0_0_2px_rgba(31,145,216,0.12)]"
                          : "border-myteengig-border hover:border-myteengig-primary/60 hover:bg-myteengig-primary/5",
                        lockAccountType && !isSelected && "cursor-not-allowed opacity-60 hover:border-myteengig-border hover:bg-white",
                      )}
                    >
                      <input
                        type="radio"
                        name="signup-account-type-option"
                        value={option.value}
                        aria-label={option.title}
                        checked={isSelected}
                        disabled={lockAccountType && !isSelected}
                        onChange={(event) => {
                          setAccountType(event.target.value as AccountType);
                          setLocalError(null);
                        }}
                        className="sr-only"
                      />
                      <span className="block min-w-0">
                        <span className="block text-sm font-semibold text-myteengig-text-primary sm:text-base">{option.title}</span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-myteengig-text-muted sm:mt-1 sm:text-sm">
                          {option.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          </div>
        ) : null}

        <div className="mt-4 max-w-md">
          <fieldset>
            <legend className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            Date of birth
            </legend>
            <input id="signup-date-of-birth" type="hidden" value={dateOfBirth} readOnly />
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1.3fr)_minmax(0,0.8fr)_minmax(0,1fr)]">
              <div>
                <select
                  id="signup-birth-month"
                  required
                  disabled={!role}
                  value={birthMonth}
                  onChange={(event) => {
                    setBirthMonth(event.target.value);
                    setLocalError(null);
                  }}
                  className="w-full rounded-md border border-myteengig-border bg-white px-2.5 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary disabled:bg-myteengig-background sm:px-3"
                >
                  <option value="">Month</option>
                  {monthOptions.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  id="signup-birth-day"
                  required
                  disabled={!role}
                  value={birthDay}
                  onChange={(event) => {
                    setBirthDay(event.target.value);
                    setLocalError(null);
                  }}
                  className="w-full rounded-md border border-myteengig-border bg-white px-2.5 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary disabled:bg-myteengig-background sm:px-3"
                >
                  <option value="">Day</option>
                  {dayOptions.map((day) => (
                    <option key={day} value={day}>
                      {Number.parseInt(day, 10)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  id="signup-birth-year"
                  required
                  disabled={!role}
                  value={birthYear}
                  onChange={(event) => {
                    setBirthYear(event.target.value);
                    setLocalError(null);
                  }}
                  className="w-full rounded-md border border-myteengig-border bg-white px-2.5 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary disabled:bg-myteengig-background sm:px-3"
                >
                  <option value="">Year</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </fieldset>
          {!role ? (
            <p className="mt-1 text-xs text-myteengig-text-muted">Choose your account type first to unlock date-of-birth options.</p>
          ) : null}
        </div>
      </section>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="signup-first-name" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            First name
          </label>
          <input
            id="signup-first-name"
            type="text"
            autoComplete="given-name"
            required
            maxLength={100}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            onBlur={() => setFirstName((current) => normalizePersonName(current))}
            className="w-full rounded-md border border-myteengig-border bg-white px-2.5 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary sm:px-3"
            placeholder="Alex"
          />
        </div>

        <div>
          <label htmlFor="signup-last-name" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            Last name
          </label>
          <input
            id="signup-last-name"
            type="text"
            autoComplete="family-name"
            required
            maxLength={100}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            onBlur={() => setLastName((current) => normalizePersonName(current))}
            className="w-full rounded-md border border-myteengig-border bg-white px-2.5 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary sm:px-3"
            placeholder="Taylor"
          />
        </div>
      </div>

      <div>
        <label htmlFor="signup-email" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
          Email
        </label>
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          required
          disabled={lockEmail}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="w-full rounded-md border border-myteengig-border bg-white px-2.5 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary sm:px-3"
          placeholder="you@example.com"
          />
      </div>

      <UsLocationFields
        idPrefix="signup"
        city={city}
        state={state}
        zip={zip}
        required
        onCityChange={setCity}
        onStateChange={setState}
        onZipChange={setZip}
      />

      {role === "TEEN" ? (
        <section className="rounded-xl border border-myteengig-border bg-myteengig-background/60 p-3 sm:p-4">
          <label htmlFor="signup-teen-bio" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
            About you (optional)
          </label>
          <textarea
            id="signup-teen-bio"
            rows={5}
            maxLength={500}
            value={bio}
            onChange={(event) => setBio(event.target.value)}
            className="w-full rounded-md border border-myteengig-border bg-white px-2.5 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary sm:px-3"
            placeholder="Share what kind of work you enjoy, when you are available, and what makes you dependable."
          />
          <p className="mt-2 text-xs text-myteengig-text-muted">{bio.length}/500 characters</p>
        </section>
      ) : null}

      {role === "ADULT" && accountType === "BUSINESS" ? (
        <section className="space-y-3 rounded-xl border border-myteengig-border bg-myteengig-background/60 p-3 sm:p-4">
          <div>
            <label htmlFor="signup-business-name" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
              Business name
            </label>
            <input
              id="signup-business-name"
              type="text"
              required
              maxLength={160}
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
              className="w-full rounded-md border border-myteengig-border bg-white px-2.5 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary sm:px-3"
              placeholder="North Shore Garden Center"
            />
          </div>
          <div>
            <label htmlFor="signup-business-website" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
              Website
            </label>
            <input
              id="signup-business-website"
              type="url"
              maxLength={255}
              value={businessWebsite}
              onChange={(event) => setBusinessWebsite(event.target.value)}
              className="w-full rounded-md border border-myteengig-border bg-white px-2.5 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary sm:px-3"
              placeholder="https://example.com"
            />
          </div>
          <div>
            <label htmlFor="signup-business-description" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
              Business description
            </label>
            <textarea
              id="signup-business-description"
              maxLength={1200}
              rows={4}
              value={businessDescription}
              onChange={(event) => setBusinessDescription(event.target.value)}
              className="w-full rounded-md border border-myteengig-border bg-white px-2.5 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary sm:px-3"
              placeholder="Tell teens what your business does and the kind of help you usually need."
            />
          </div>
          <label
            htmlFor="signup-business-volunteer-approval-requested"
            className="flex items-start gap-3 rounded-lg border border-myteengig-border bg-white p-3 text-sm leading-relaxed text-myteengig-text-muted"
          >
            <input
              id="signup-business-volunteer-approval-requested"
              type="checkbox"
              checked={businessVolunteerApprovalRequested}
              onChange={(event) => {
                setBusinessVolunteerApprovalRequested(event.target.checked);
                setLocalError(null);
              }}
              className="mt-1 h-4 w-4 shrink-0 rounded border-myteengig-border text-myteengig-primary focus:ring-myteengig-primary"
            />
            <span>
              <span className="block font-semibold text-myteengig-text-primary">Nonprofit volunteer posting approval</span>
              <span className="mt-1 block">
                We are a nonprofit or not-for-profit organization requesting approval to post volunteer opportunities.
              </span>
            </span>
          </label>
        </section>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="signup-password" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            Password
          </label>
          <PasswordField
            id="signup-password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={72}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            placeholder="Create a password"
          />
        </div>

        <div>
          <label htmlFor="signup-confirm-password" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            Confirm password
          </label>
          <PasswordField
            id="signup-confirm-password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={72}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            placeholder="Re-enter your password"
          />
        </div>
      </div>

      <div className="rounded-lg border border-myteengig-border bg-myteengig-background/60 p-3">
        <label htmlFor="signup-terms-accepted" className="flex items-start gap-3 text-sm leading-relaxed text-myteengig-text-muted">
          <input
            id="signup-terms-accepted"
            type="checkbox"
            required
            checked={termsAccepted}
            onChange={(event) => {
              setTermsAccepted(event.target.checked);
              setLocalError(null);
            }}
            className="mt-1 h-4 w-4 shrink-0 rounded border-myteengig-border text-myteengig-primary focus:ring-myteengig-primary"
          />
          <span>
            I agree to the{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-myteengig-primary underline underline-offset-2 hover:text-myteengig-primary-hover"
            >
              Terms of Service
            </a>
            .
          </span>
        </label>
      </div>

      {localError ? (
        <p role="alert" className="text-sm text-red-700">
          {localError}
        </p>
      ) : null}

      {errorMessage ? (
        <p role="alert" className="text-sm text-red-700">
          {errorMessage}
        </p>
      ) : null}

      <Button type="submit" variant="primary" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? "Creating account..." : submitLabel}
      </Button>
    </form>
  );
};

export default SignupForm;
